<template>
  <b-modal
    :visible="visible"
    :title="title"
    body-class="bg-white"
    @hide="close"
    data-test="export-chat-modal"
  >
    <p v-if="scope === 'allRooms'" class="text-muted small">
      This will export <strong>all team chat rooms</strong> for this site.
      Large sites may take a few minutes.
    </p>

    <div class="form-group">
      <label class="font-weight-bold">Format</label>
      <div class="form-check">
        <input
          id="export-format-pdf"
          v-model="format"
          type="radio"
          value="pdf"
          class="form-check-input"
          :disabled="submitting"
          data-test="export-format-pdf"
        />
        <label for="export-format-pdf" class="form-check-label">
          PDF <span class="text-muted small">(recommended — printable, searchable)</span>
        </label>
      </div>
      <div class="form-check">
        <input
          id="export-format-txt"
          v-model="format"
          type="radio"
          value="txt"
          class="form-check-input"
          :disabled="submitting"
          data-test="export-format-txt"
        />
        <label for="export-format-txt" class="form-check-label">
          Plain text (.txt) <span class="text-muted small">(lightweight, plain-text transcript)</span>
        </label>
      </div>
    </div>

    <div class="form-group">
      <div class="form-check">
        <input
          id="export-include-attachments"
          v-model="includeAttachments"
          type="checkbox"
          class="form-check-input"
          :disabled="submitting"
          data-test="export-include-attachments"
        />
        <label for="export-include-attachments" class="form-check-label">
          Include file attachments <span class="text-muted small">(downloads as a .zip)</span>
        </label>
      </div>
      <small class="form-text text-muted">
        Attachments older than 90 days are no longer available and will be noted in
        the export. Total attachment size is capped at 1 GB per export.
      </small>
    </div>

    <div class="mt-3 p-3" style="background:#F8FAFC; border-radius:6px;">
      <div class="small text-muted mb-1">We'll email the download link to:</div>
      <strong>{{ requesterEmail || '(your account email)' }}</strong>
      <div class="small text-muted mt-2">
        The link works for <strong>7 days</strong>, then it's automatically deleted for security.
      </div>
    </div>

    <template #modal-footer>
      <button
        class="btn btn-secondary"
        :disabled="submitting"
        @click="close"
        data-test="export-cancel-btn"
      >
        Cancel
      </button>
      <button
        class="btn btn-primary"
        :disabled="submitting || !requesterEmail"
        @click="submit"
        data-test="export-submit-btn"
      >
        <b-spinner v-if="submitting" small class="mr-2" />
        {{ submitting ? 'Starting…' : 'Start Export' }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { extractApiError } from '@/utils/apiError';

export default {
  name: 'ExportChatModal',
  props: {
    visible: { type: Boolean, default: false },
    scope: { type: String, required: true }, // 'room' | 'allRooms'
    roomId: { type: String, default: null },
    roomName: { type: String, default: null },
    siteId: { type: String, required: true },
    siteName: { type: String, default: null },
    timezone: { type: String, default: null },
    requesterEmail: { type: String, required: true },
    requesterName: { type: String, default: null },
  },
  data() {
    return {
      format: 'pdf',
      includeAttachments: true,
      submitting: false,
    };
  },
  computed: {
    title() {
      if (this.scope === 'allRooms') return 'Export All Team Chats';
      return this.roomName ? `Export Chat: ${this.roomName}` : 'Export Chat';
    },
  },
  methods: {
    ...mapActions('chat', ['_startChatExport']),
    close() {
      if (this.submitting) return; // can't dismiss mid-submit
      this.$emit('close');
    },
    async submit() {
      this.submitting = true;
      const payload = {
        siteId: this.siteId,
        scope: this.scope,
        roomId: this.scope === 'room' ? this.roomId : undefined,
        format: this.format,
        includeAttachments: this.includeAttachments,
        requesterEmail: this.requesterEmail,
        requesterName: this.requesterName,
        siteName: this.siteName,
        timezone: this.timezone,
      };
      try {
        const result = await this._startChatExport(payload);
        // Remember the pending job so the Chat page header can show the
        // status pill until the email arrives (or 15 min, whichever first).
        try {
          localStorage.setItem('teamio.chatExport.pending', JSON.stringify({
            exportId: result.exportId,
            requestedAt: Date.now(),
          }));
        } catch (e) { /* localStorage may be disabled */ }

        this.$bvToast.toast(
          `We're building your export now. You'll get an email at ${this.requesterEmail} with the download link — usually within 5 minutes (longer for "All Team Chats" or large attachment bundles).`,
          {
            title: 'Export started',
            variant: 'success',
            solid: true,
            autoHideDelay: 10000, // 10s — longer than default so users read the email line
            toaster: 'b-toaster-top-right',
          }
        );
        this.$emit('close');
      } catch (e) {
        const msg = extractApiError(e, 'Could not start the export.');
        this.$bvToast.toast(msg, {
          title: 'Export failed to start',
          variant: 'danger',
          solid: true,
          autoHideDelay: 10000,
          toaster: 'b-toaster-top-right',
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
